"use client";

import React from "react";
import { ZoomIn, ArrowRight, Info } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const galleryItems = [{
  id: "1",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/pistachio_gelato_scoop.png",
  alt: "Artisanal Pistachio Gelato",
  title: "Artisanal Gelato",
  description: "Our signature pistachio gelato is crafted using traditional methods and the finest nuts, offering a creamy texture that melts perfectly under the Saranda sun.",
  aspectClass: "aspect-square"
}, {
  id: "2",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/espresso_macchiato_morning.png",
  alt: "Morning Coffee by the Sea",
  title: "Morning Rituals",
  description: "Start your day with our premium Albanian-roasted coffee. A perfect balance of bold aroma and smooth finish, served with a view of the turquoise horizon.",
  aspectClass: "aspect-video"
}, {
  id: "3",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/blue_lagoon_cocktail.png",
  alt: "Refreshing Blue Cocktail",
  title: "Sunset Spirits",
  description: "As the sun dips below the Ionian Sea, our mixologists craft vibrant cocktails that capture the colors of the Mediterranean in a glass.",
  aspectClass: "aspect-square"
}, {
  id: "4",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/strawberry_tart_pastry.png",
  alt: "Fresh Strawberry Tart",
  title: "Sweet Paradise",
  description: "Handmade daily in our pastry kitchen, our fruit tarts feature buttery crusts and seasonal berries picked at the peak of ripeness.",
  aspectClass: "aspect-video"
}, {
  id: "5",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/interior_cafe_vibes.png",
  alt: "Cafe Interior Design",
  title: "Pastel Sanctuary",
  description: "Step into a world of baby pinks and blues. Our interior is designed to be a cool, airy escape from the summer heat, where every corner is a postcard.",
  aspectClass: "aspect-square"
}, {
  id: "6",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/iced_latte_condensation.png",
  alt: "Iced Latte with Cream",
  title: "Chilled Perfection",
  description: "Our iced lattes are a local favorite, featuring double-shot espresso poured over crystal-clear ice and topped with a silky milk cloud.",
  aspectClass: "aspect-video"
}];
export default function GalleriaSommersa() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224685" id="postcards-from-paradise" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/10 rounded-lg blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mb-16 transition-all duration-700", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary text-secondary-foreground mb-6">
            <Info className="size-4" />
            <span className="text-sm font-default font-medium tracking-wider uppercase">The Sweet Collection</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-6 leading-tight">
            Postcards from Paradise
          </h2>
          <p className="text-xl text-muted-foreground font-default leading-relaxed">
            A visual journey through the flavors and feelings of Frozen Paradise. From the first scoop of gelato to the last sip of a sunset cocktail in Saranda.
          </p>
        </div>

        {}
        <div ref={gridRef} className={cn("columns-1 md:columns-2 lg:columns-3 gap-4 transition-all duration-1000 delay-200", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid mb-4 rounded-lg overflow-hidden bg-muted relative group shadow-md hover:shadow-xl transition-shadow duration-500">
              <Dialog>
                <DialogTrigger asChild>
                  <div className={cn("relative w-full cursor-pointer block", item.aspectClass)}>
                    <img src={item.src} alt={item.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 sepia-[0.1] saturate-[1.1] brightness-[1.02]" loading="lazy" />
                    {}
                    <div className="absolute inset-0 bg-primary/40 backdrop-blur-[2px] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    
                    {}
                    <div className="absolute inset-0 p-6 flex flex-col justify-end items-start">
                      <div className="bg-white/30 backdrop-blur-md text-white p-2 rounded-lg mb-4 opacity-0 -translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 border border-white/20">
                        <ZoomIn className="size-5" />
                      </div>
                      <h3 className="text-white font-default font-semibold text-xl md:text-2xl leading-tight translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </DialogTrigger>
                
                {}
                <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-background border-border shadow-2xl rounded-lg">
                  <div className="flex flex-col md:flex-row h-full max-h-[85vh]">
                    {}
                    <div className="relative w-full md:w-3/5 h-64 md:h-auto bg-muted">
                      <img src={item.src} alt={item.alt} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:hidden" />
                    </div>
                    
                    {}
                    <div className="w-full md:w-2/5 p-6 md:p-8 flex flex-col overflow-y-auto bg-background">
                      <DialogHeader className="mb-6 text-left">
                        <div className="text-sm font-default font-medium text-primary mb-2 uppercase tracking-widest">
                          Taste of Saranda
                        </div>
                        <DialogTitle className="text-2xl md:text-4xl font-default text-foreground mb-4 leading-tight">
                          {item.title}
                        </DialogTitle>
                        <DialogDescription className="text-lg text-muted-foreground font-default leading-relaxed">
                          {item.description}
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="mt-auto pt-8 border-t border-border">
                        <Button asChild className="w-full group/btn bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg" size="lg">
                          <Link to="/OurMenuPage">
                            View Full Menu
                            <ArrowRight className="size-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}